// MessagePanelBottom.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import NeedAuth from '../container/NeedAuth.js';
import MessageMeta from '../container/MessageMeta.js';
import MessageInput from '../container/MessageReplyInput.js';
import UnlockButton from '../component/UnlockButton.jsx';
import WithRealTimeStatus from '../container/WithRealTimeStatus.js';

import ChatBtnSrc from '../../img/btn_chat_24.svg';
import GiftIcon from '../../img/ic_gift_fill.svg';

import { ButtonId } from '../resource/trackingConstants.js';
import withNotificationPaddingMonitor from '../resource/withNotificationPaddingMonitor.js';
import { DrmFeatureName } from '../resource/drmConstants.js';
import { MessageCtaType } from '../resource/messageConstants.js';
import media from '../style/media.js';

import { QUICKPAY_BUNDLE_SECTION } from '../resource/shopConstants.js';

export class MessagePanelBottom extends React.PureComponent {
  handleGiftClick = () => {
    const { userId, isAuthed, login, onGiftClick } = this.props;
    if (isAuthed) {
      onGiftClick(userId);
    } else {
      login();
    }
  };

  handleChatAudioClick = () => {
    const { userId, isAuthed, login, sendChatStart } = this.props;
    if (isAuthed) {
      sendChatStart(userId);
    } else {
      login();
    }
  };

  handleClickChat = () => {
    const { userId, sendChatStart } = this.props;
    return sendChatStart(userId);
  };

  handleUnlockButtonClick = event => {
    const {
      id,
      isBalanceInsufficient,
      type,
      unlockButtonStatus,
      isAuthed,
      ctaUrlData,
      sendUnlockMessage,
      hasPurchased,
      openQuickpayModal,
    } = this.props;
    const drmFeatureName = ['chat', 'reply'].includes(type)
      ? DrmFeatureName.CHAT
      : DrmFeatureName.STORY;
    if (unlockButtonStatus === 'loading') {
      return event.stopPropagation();
    } else if (!isAuthed || (!hasPurchased && isBalanceInsufficient)) {
      return openQuickpayModal({
        sectionId: QUICKPAY_BUNDLE_SECTION.STORY,
      });
    } else {
      return sendUnlockMessage({
        id,
        shouldCheckDrm: true,
        shouldOpenMessagePackUpsellingModal:
          MessageCtaType.PRODUCT === ctaUrlData.type,
        drmFeatureName,
      });
    }
  };

  renderUnlockButton() {
    const {
      isMe,
      isAuthed,
      isBalanceInsufficient,
      unlockPrice,
      showUnlockButton,
      hasPurchased,
      id,
    } = this.props;

    if (!showUnlockButton || isMe) {
      return null;
    }

    const shouldShowPrice = hasPurchased || !isBalanceInsufficient;
    const shouldShowShimmer = !hasPurchased && isBalanceInsufficient;

    return (
      <OperationRow>
        {isAuthed && (
          <ChatBtnWrapper
            data-element_id={ButtonId.All.ButtonChat}
            data-tracking_payload={{ messageId: id }}
            onClick={this.handleClickChat}
          >
            <ChatBtn>
              <img src={ChatBtnSrc} alt="chat" />
            </ChatBtn>
          </ChatBtnWrapper>
        )}
        <UnlockWrapper>
          <UnlockButton
            data-element_id={
              isAuthed
                ? ButtonId.All.ButtonUnlock
                : ButtonId.Detail.ButtonDetailSignup
            }
            data-tracking_payload={{ messageId: id }}
            status={this.props.unlockButtonStatus}
            price={unlockPrice}
            i18nKey={shouldShowPrice ? null : 'cta_unlock_story_zero_purchase'}
            shouldShowPrice={shouldShowPrice}
            shouldShowShimmer={shouldShowShimmer}
            onClick={event => {
              this.handleUnlockButtonClick(event);
            }}
          />
        </UnlockWrapper>
        {isAuthed && (
          <GiftBtnWrapper
            data-element_id={ButtonId.All.ButtonGift}
            data-tracking_payload={{ messageId: id }}
            onClick={this.handleGiftClick}
          >
            <GiftBtn>
              <img src={GiftIcon} alt="gift" />
            </GiftBtn>
          </GiftBtnWrapper>
        )}
      </OperationRow>
    );
  }

  renderInput() {
    const { id, userId, isMe, isSending, showInput, setExpanded } = this.props;
    if (!showInput || isMe) {
      return null;
    }

    return (
      <NeedAuth>
        <OperationRow data-key="needAuth">
          <UnlockWrapper>
            <UnlockButton
              status="unauthorized"
              i18nKey="message_detail_login"
              data-element_id={ButtonId.Detail.ButtonFreeDetailSignup}
              data-tracking_payload={{ messageId: id }}
              onClick={event => {
                this.handleUnlockButtonClick(event);
              }}
            />
          </UnlockWrapper>
        </OperationRow>

        <div data-key="authing" />
        <MessageInputContainer data-key="authed">
          <MessageInputWrapper>
            <WithRealTimeStatus userId={userId}>
              {({ replyPrice }) => (
                <MessageInput
                  showAudio={false}
                  userId={userId}
                  isLoading={isSending}
                  messageId={id}
                  cost={replyPrice}
                  onFocus={() =>
                    setExpanded({ expandedField: 'input', messageId: id })
                  }
                  onBlur={() =>
                    setExpanded({ expandedField: null, messageId: id })
                  }
                  directToChatGift={this.handleGiftClick}
                  directToChatByAudio={this.handleChatAudioClick}
                />
              )}
            </WithRealTimeStatus>
          </MessageInputWrapper>
        </MessageInputContainer>
      </NeedAuth>
    );
  }

  render() {
    const {
      id,
      showMeta,
      isFree,
      isUnlocked,
      isFreeZone,
      showUnlockButton,
      showInput,
    } = this.props;

    const showOperation = showInput || showUnlockButton;

    return (
      <StyledMessagePanelBottom>
        {showMeta && (
          <MetaRow isBottom={!showOperation}>
            <MessageMeta
              id={id}
              showExpires={(!isUnlocked || isFree) && !isFreeZone}
              showUnlocks={!isFree && !isFreeZone}
              showViewCount={isFree || isFreeZone}
              showLikes={!isFree && !isFreeZone}
            />
          </MetaRow>
        )}
        {this.renderUnlockButton()}
        {this.renderInput()}
      </StyledMessagePanelBottom>
    );
  }
}

MessagePanelBottom.propTypes = {
  id: PropTypes.string,
  isBalanceInsufficient: PropTypes.bool,
  isMe: PropTypes.bool,
  userId: PropTypes.string,
  type: PropTypes.string,
  isAuthed: PropTypes.bool,
  isSending: PropTypes.bool,
  isFree: PropTypes.bool,
  isUnlocked: PropTypes.bool,
  isFreeZone: PropTypes.bool,
  showMeta: PropTypes.bool,
  showUnlockButton: PropTypes.bool,
  showInput: PropTypes.bool,
  unlockButtonStatus: PropTypes.string,
  unlockPrice: PropTypes.number,
  ctaUrlData: PropTypes.object,
  hasPurchased: PropTypes.bool,
  sendChatStart: PropTypes.func,
  onGiftClick: PropTypes.func,
  login: PropTypes.func,
  sendUnlockMessage: PropTypes.func,
  setExpanded: PropTypes.func,
  openQuickpayModal: PropTypes.func,
};

MessagePanelBottom.defaultProps = {
  id: '',
  isBalanceInsufficient: false,
  isMe: false,
  userId: '',
  type: null,
  isAuthed: false,
  isSending: false,
  isFree: false,
  isUnlocked: false,
  isFreeZone: false,
  showMeta: false,
  showUnlockButton: false,
  showInput: false,
  unlockPrice: 0,
  unlockButtonStatus: '',
  ctaUrlData: {},
  hasPurchased: false,
  sendChatStart: () => null,
  onGiftClick: () => null,
  login: () => null,
  setExpanded: () => null,
  sendUnlockMessage: () => null,
  openQuickpayModal: () => null,
};

const StyledMessagePanelBottom = styled.div`
  pointer-events: auto;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px 0;
  margin-bottom: ${({ isBottom }) => (isBottom ? 24 : 22)}px;
  ${media.mobile`
    margin-bottom: ${({ isBottom }) => (isBottom ? 24 : 16)}px;
  `}
`;

const OperationRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0 16px 20px 16px;
  ${media.mobile`
    padding: 0 16px 16px 16px;
  `}
`;

const MessageInputContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  padding: 0 16px 20px 16px;
  ${media.mobile`
    padding: 0 16px 16px 16px;
  `}
`;

const MessageInputWrapper = styled.div`
  flex: auto;
  width: 100%;
`;

const ChatBtnWrapper = styled.button`
  flex: none;
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-image: linear-gradient(
    136deg,
    #00d2be 14%,
    #5d82bc 52%,
    #8e37d7 86%
  );
  outline: none;
  border: 0;
  margin-right: 16px;
  ${media.mobile`
    margin-right: 12px;
  `}
`;
const ChatBtn = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  img {
    width: 100%;
    height: 100%;
  }
`;

const UnlockWrapper = styled.div`
  flex: auto;
  width: 100%;
`;

const GiftBtnWrapper = styled.button`
  flex: none;
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-image: linear-gradient(
    136deg,
    #00d2be 14%,
    #5d82bc 52%,
    #8e37d7 86%
  );
  outline: none;
  border: 0;
  margin-left: 12px;
`;
const GiftBtn = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  img {
    width: 100%;
    height: 100%;
  }
`;

export default withTranslation()(
  withNotificationPaddingMonitor(MessagePanelBottom)
);
